import { useEffect } from 'react';
import { INPUT_TYPE } from '../utils';

const RENDER_PROPS = ['component', 'render', 'children'];

// It receives react final form object as an argument, and uses form
// state to calculate whether the form is dirty (returns a boolean).
// We cannot simply use the 'dirty' value from the form state
// because we want to ignore cases when value is changed programmatically
const calculateDirty = (form) => {
  const { dirtyFields, modified, submitting } = form.getState();
  // check if there are dirty fields, and make sure that they are
  // edited by user, and not programmatically
  return Object.keys(dirtyFields).some(
    (field) => !submitting && dirtyFields[field] && modified[field]
  );
};

// a function that returns modified render prop for the Final Form component
// this way we are able to bake in "prevent leave page" functionality directly
// into the Form component. If the `forcePreventUnload` is false we decide onbeforeunload
// value based on the 'dirtyFields' value
const renderWrapper =
  (render, forcePreventUnload, formId, setDirty) => (props) => {
    const isDirty = forcePreventUnload ? true : calculateDirty(props.form);
    useEffect(() => {
      setDirty({ id: formId, isDirty: isDirty });
    }, [isDirty]);

    return render(props);
  };

// A wrapper function that return props, and modify some of them if necessary.
export const handleProps = (
  props,
  preventUnload,
  forcePreventUnload,
  idRef,
  setDirty
) => {
  if (preventUnload || forcePreventUnload) {
    RENDER_PROPS.some((renderProp) => {
      if (props[renderProp]) {
        props[renderProp] = renderWrapper(
          props[renderProp],
          forcePreventUnload,
          idRef.current,
          setDirty
        );
        return true;
      }
    });
  }
  const { onSubmit } = props;

  props.onSubmit = async (values, form, callback) => {
    // here we make sure set form to not dirty before
    // submitting. This will allow us to switch page on
    // successfull submit, given that form in question is
    // the only form on page, or all the other forms are
    // not dirty as well
    await setDirty({ id: idRef.current, isDirty: false });

    // here we transform decimal values from string to number
    // if the type of the field is set to `number`
    const normalizedValues = normalizeValues({ form, values });

    const errors = await onSubmit(normalizedValues, form, callback);

    // restore the value of onbeforeunload if there are errors,
    // since we've never left the page
    if (errors) {
      setDirty({ id: idRef.current, isDirty: calculateDirty(form) });
    }

    return errors;
  };
  return props;
};

// we deal with currency values which could be very hard to format and display
// properly using the native number input. For this reason we are handling these
// and other decimal values as string. However, backend expects them to be a proper
// number, that is why we cast them from string to number before calling the
// onSubmit function. We are not affecting the actual field values,
// thus keeping the formating simple, while provideing backend with the correct values.
// We recognise the number fields by checking the field's data object,
// namely the `type` property, that is propagated to the fields data prop.
// see ../field.js
const normalizeValues = ({ form, values }) => {
  const numberFields = form
    .getRegisteredFields()
    .map((fieldName) => form.getFieldState(fieldName))
    .filter((field) => field?.data?.type === INPUT_TYPE.NUMBER);

  const normalizedNumberValues = numberFields.reduce((acc, field) => {
    const value = values[field.name];
    return { ...acc, [field.name]: value ? Number(value) : null };
  }, {});

  return { ...values, ...normalizedNumberValues };
};
